import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, type TargetAndTransition } from 'framer-motion';
import { X, Heart, Gift, Sparkles } from 'lucide-react';
import { useDonation } from '@/context/DonationContext';
import { useEffectState } from '@/hooks/useEffectState';
import type {
  DonationAnimationStyle,
  DonationDisplayPosition,
  DonationClickAction,
} from '@/types/donation';
import { ContentPreviewModal } from '@/components/donation/ContentPreviewModal';
import { DonationMessageModal } from '@/components/donation/DonationMessageModal';
import { getContentRoute } from '@/utils/contentNavigation';

interface ExecuteClickActionContext {
  clickPrompt: () => void;
  onDonateClick: () => void;
  navigate: (route: string) => void;
  showGratitude: () => void;
  setShowContentPreview: () => void;
  setShowFullMessage: () => void;
}

function runDefaultBehavior(ctx: ExecuteClickActionContext) {
  ctx.clickPrompt();
  ctx.onDonateClick();
}

function runExternalLink(action: DonationClickAction, ctx: ExecuteClickActionContext) {
  if (action.externalUrl) {
    window.open(action.externalUrl, '_blank', 'noopener,noreferrer');
  }
  ctx.clickPrompt();
  ctx.showGratitude();
}

function runInternalContent(action: DonationClickAction, ctx: ExecuteClickActionContext) {
  if (!action.contentType || !action.contentId) return;
  if (action.showPreview) {
    ctx.setShowContentPreview();
  } else {
    ctx.navigate(getContentRoute(action.contentType, action.contentId));
    ctx.clickPrompt();
  }
}

function runFullMessage(action: DonationClickAction, ctx: ExecuteClickActionContext) {
  if (action.blocks && action.blocks.length > 0) {
    ctx.setShowFullMessage();
  } else {
    runDefaultBehavior(ctx);
  }
}

const ACTION_HANDLERS: Record<
  DonationClickAction['type'],
  (a: DonationClickAction, ctx: ExecuteClickActionContext) => void
> = {
  external_link: runExternalLink,
  internal_content: runInternalContent,
  full_message: runFullMessage,
};

function executeClickAction(
  action: DonationClickAction | null,
  ctx: ExecuteClickActionContext
): void {
  if (!action) {
    runDefaultBehavior(ctx);
    return;
  }
  const handler = ACTION_HANDLERS[action.type];
  if (handler) {
    handler(action, ctx);
  } else {
    runDefaultBehavior(ctx);
  }
}

// ============================================
// ANIMATION VARIANTS
// ============================================

const animationVariants: Record<DonationAnimationStyle, TargetAndTransition> = {
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
  bounce: {
    y: [0, -8, 0],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
  glow: {
    boxShadow: [
      '0 0 0 0 rgba(142, 69, 133, 0.4)',
      '0 0 20px 10px rgba(142, 69, 133, 0.2)',
      '0 0 0 0 rgba(142, 69, 133, 0.4)',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
  none: {},
};

const positionStyles: Record<DonationDisplayPosition, string> = {
  'top-right': 'fixed top-20 right-4 z-[100]',
  'bottom-right': 'fixed bottom-24 right-4 z-[100]',
  'header-inline': '', // This is handled differently - rendered in Header
};

// ============================================
// ICON COMPONENT
// ============================================

interface DonationIconProps {
  icon: string;
  size?: number;
}

function DonationIcon({ icon, size = 24 }: DonationIconProps) {
  // Check if it's an emoji (starts with an emoji character)
  const isEmoji = /^\p{Emoji}/u.test(icon);

  if (isEmoji) {
    return <span className="text-2xl">{icon}</span>;
  }

  // Map icon names to components
  switch (icon) {
    case 'heart':
      return <Heart size={size} className="text-white" fill="currentColor" />;
    case 'gift':
      return <Gift size={size} className="text-white" />;
    case 'sparkles':
      return <Sparkles size={size} className="text-white" />;
    default:
      return <Heart size={size} className="text-white" fill="currentColor" />;
  }
}

// ============================================
// FLOATING BADGE COMPONENT
// ============================================

interface FloatingBadgeProps {
  onDonateClick: () => void;
}

export function DonationBadge({ onDonateClick }: FloatingBadgeProps) {
  const navigate = useNavigate();
  const { activePrompt, dismissPrompt, clickPrompt, config, effectiveClickAction } = useDonation();
  const { showGratitude } = useEffectState();
  const [isExpanded, setIsExpanded] = useState(false);

  // Modal states
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [showFullMessage, setShowFullMessage] = useState(false);

  // Auto-expand on new prompt
  useEffect(() => {
    if (activePrompt) {
      // Delay expansion for better UX
      const timer = setTimeout(() => setIsExpanded(true), 500);
      return () => clearTimeout(timer);
    } else {
      setIsExpanded(false);
    }
  }, [activePrompt]);

  // Don't render if no active prompt or if position is header-inline
  if (!activePrompt || config?.display.position === 'header-inline') {
    return null;
  }

  const position = config?.display.position || 'bottom-right';
  const animationStyle = config?.display.animationStyle || 'pulse';

  const handleClick = () => {
    executeClickAction(effectiveClickAction, {
      clickPrompt,
      onDonateClick,
      navigate,
      showGratitude,
      setShowContentPreview: () => setShowContentPreview(true),
      setShowFullMessage: () => setShowFullMessage(true),
    });
  };

  const handleContentPreviewClose = () => {
    setShowContentPreview(false);
    clickPrompt();
  };

  const handleFullMessageClose = () => {
    setShowFullMessage(false);
    clickPrompt();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismissPrompt();
  };

  return (
    <AnimatePresence>
      <motion.div
        className={positionStyles[position]}
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      >
        <motion.div className="relative" animate={animationVariants[animationStyle]}>
          {/* Main Badge - using div with role for click, separate dismiss button */}
          <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }}
            className="group relative flex items-center gap-3 bg-gradient-to-r from-[#8e4585] to-[#a855a0] text-white rounded-full shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
          >
            {/* Icon Circle */}
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20">
              <DonationIcon icon={activePrompt.trigger.icon} />
            </div>

            {/* Expanded Message */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 'auto', opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pr-10 py-2 max-w-[200px]">
                    <p className="text-sm font-medium leading-tight whitespace-normal">
                      {activePrompt.trigger.message}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Dismiss Button - moved outside the clickable div */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-white text-slate-500 rounded-full shadow-md hover:bg-slate-100 hover:text-slate-700 transition-colors z-10"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>

          {/* Decorative Sparkles */}
          <motion.div
            className="absolute -top-1 -left-1 text-yellow-300"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 15, -15, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <Sparkles size={16} />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Content Preview Modal */}
      {effectiveClickAction?.type === 'internal_content' &&
        effectiveClickAction.contentType &&
        effectiveClickAction.contentId && (
          <ContentPreviewModal
            isOpen={showContentPreview}
            onClose={handleContentPreviewClose}
            contentType={effectiveClickAction.contentType}
            contentId={effectiveClickAction.contentId}
          />
        )}

      {/* Full Message Modal */}
      {effectiveClickAction?.type === 'full_message' && effectiveClickAction.blocks && (
        <DonationMessageModal
          isOpen={showFullMessage}
          onClose={handleFullMessageClose}
          blocks={effectiveClickAction.blocks}
        />
      )}
    </AnimatePresence>
  );
}

// ============================================
// HEADER INLINE BADGE (for header-inline position)
// ============================================

interface HeaderDonationBadgeProps {
  onDonateClick: () => void;
}

export function HeaderDonationBadge({ onDonateClick }: HeaderDonationBadgeProps) {
  const navigate = useNavigate();
  const { activePrompt, dismissPrompt, clickPrompt, config, effectiveClickAction } = useDonation();
  const { showGratitude } = useEffectState();
  const [showTooltip, setShowTooltip] = useState(false);

  // Modal states
  const [showContentPreview, setShowContentPreview] = useState(false);
  const [showFullMessage, setShowFullMessage] = useState(false);

  // Only render if position is header-inline and there's an active prompt
  if (!activePrompt || config?.display.position !== 'header-inline') {
    return null;
  }

  const animationStyle = config?.display.animationStyle || 'pulse';

  const handleClick = () => {
    setShowTooltip(false);
    executeClickAction(effectiveClickAction, {
      clickPrompt,
      onDonateClick,
      navigate,
      showGratitude,
      setShowContentPreview: () => setShowContentPreview(true),
      setShowFullMessage: () => setShowFullMessage(true),
    });
  };

  const handleContentPreviewClose = () => {
    setShowContentPreview(false);
    clickPrompt();
  };

  const handleFullMessageClose = () => {
    setShowFullMessage(false);
    clickPrompt();
  };

  return (
    <div className="relative">
      <motion.button
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="p-2 rounded-lg transition-all duration-200 group relative active:scale-90 text-[#8e4585] hover:bg-[#f8e8f8]"
        animate={animationVariants[animationStyle]}
        title="Support Us"
      >
        <DonationIcon icon={activePrompt.trigger.icon} size={20} />

        {/* Notification dot */}
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      </motion.button>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute right-0 top-full mt-2 w-64 p-3 bg-white rounded-xl shadow-lg border border-slate-100 z-50"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <p className="text-sm text-slate-700">{activePrompt.trigger.message}</p>
                <button
                  onClick={handleClick}
                  className="mt-2 px-3 py-1.5 bg-gradient-to-r from-[#8e4585] to-[#a855a0] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  Donate Now
                </button>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  dismissPrompt();
                }}
                className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content Preview Modal */}
      {effectiveClickAction?.type === 'internal_content' &&
        effectiveClickAction.contentType &&
        effectiveClickAction.contentId && (
          <ContentPreviewModal
            isOpen={showContentPreview}
            onClose={handleContentPreviewClose}
            contentType={effectiveClickAction.contentType}
            contentId={effectiveClickAction.contentId}
          />
        )}

      {/* Full Message Modal */}
      {effectiveClickAction?.type === 'full_message' && effectiveClickAction.blocks && (
        <DonationMessageModal
          isOpen={showFullMessage}
          onClose={handleFullMessageClose}
          blocks={effectiveClickAction.blocks}
        />
      )}
    </div>
  );
}
